import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n/config'; // Initialize i18n with LanguageDetector and localStorage persistence
import { DataFilesProvider, useDataFiles } from './components/DataFilesContext';
import { GlobalFileSelector } from './components/GlobalFileSelector';
import { DiaryProvider } from './components/DiaryContext';
import { GlobalTradeProvider } from './contexts/GlobalTradeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './components/Toast/ToastContext';
import ToastContainer from './components/Toast/ToastContainer';
import { DialogProvider } from './components/DialogProvider';
import useGlobalScroll from './hooks/useGlobalScroll';
import { SupabaseAuthService } from './services/SupabaseAuthService';
import { PasswordResetGuard, PASSWORD_RESET_PENDING_EVENT } from './services/PasswordResetGuard';
import { SessionService } from './services/SessionService';
import { ErrorLoggerService } from './services/ErrorLoggerService';
import { SessionCleanupService } from './services/SessionCleanupService';
import { TutorialService } from './services/TutorialService';
import { DonationReminderService } from './services/DonationReminderService';
import { UIStateService } from './services/UIStateService';
import { AnalyticsService } from './services/AnalyticsService';
import { UnifiedAnalyticsService } from './services/UnifiedAnalyticsService';
import { COMMUNITY_DISCORD_URL } from './config/community';
import { GoogleAnalyticsService } from './services/GoogleAnalyticsService';
import { PostHogService } from './services/PostHogService';
import { AdminAuthService } from './services/AdminAuthService';
import { isSupabaseConfigured } from './config/supabase';
import { AnalyticsConsentBanner } from './components/AnalyticsConsentBanner';
import Seo from './components/Seo';
import { extractUserTajirPlan } from './services/TajirPlanService';
import { SubscriptionTier } from './services/SubscriptionService';
import { StripeCheckoutService } from './services/StripeCheckoutService';
import {
  LayoutDashboard,
  Database,
  BookText,
  FileText,
  BrainCircuit,
  LogOut,
  Settings,
  User,
  Heart,
  MessageSquare,
  ChevronDown,
  Menu,
  X,
  Flag,
  Plug
} from 'lucide-react';

const INTERNAL_ADMIN_EMAILS = ['addoumzaid@gmail.com'];

// 🚀 LAZY LOADING: Code splitting per migliorare performance
// Ogni route viene caricata solo quando necessaria
const Dashboard = lazy(() => import('./components/Dashboard'));
const DashboardLab = lazy(() => import('./components/DashboardLab')); // DEV-ONLY widget preview harness
const OnboardingLab = lazy(() => import('./components/OnboardingLab')); // DEV-ONLY onboarding preview harness
const Diary = lazy(() => import('./components/Diary'));
const DataTable = lazy(() => import('./components/DataTable'));
const TradingReportPage = lazy(() => import('./components/ReportPage'));
const TajirAI = lazy(() => import('./components/TajirAI'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const CTraderCallback = lazy(() => import('./components/CTraderCallback'));
const PlatformsPage = lazy(() => import('./components/platforms/PlatformsPage'));
const PlatformAutoSync = lazy(() => import('./components/platforms/PlatformAutoSync'));
const AdminLoginPage = lazy(() => import('./components/AdminLoginPage'));
const AdminDashboardFunctional = lazy(() => import('./components/AdminDashboardFunctional').then(module => ({ default: module.AdminDashboardFunctional })));
const AdminMonitoring = lazy(() => import('./components/AdminMonitoring').then(module => ({ default: module.AdminMonitoring })));
const AdminLandingAnalytics = lazy(() => import('./components/AdminLandingAnalytics'));
const AdminPlatformAnalytics = lazy(() => import('./components/AdminPlatformAnalytics'));
const AdminBlogAnalytics = lazy(() => import('./components/AdminBlogAnalytics'));
const AdminRevenue = lazy(() => import('./components/AdminRevenue'));
const AdminAiCosts = lazy(() => import('./components/AdminAiCosts'));
const AdminContactMessages = lazy(() => import('./components/AdminContactMessages'));
const AdminUsers = lazy(() => import('./components/AdminUsersNew'));
const AdminClients = lazy(() => import('./components/AdminClients'));
const AdminBroadcast = lazy(() => import('./components/AdminBroadcast'));
const FeedbackCenter = lazy(() => import('./components/FeedbackCenter'));
const AdminEmailList = lazy(() => import('./components/AdminEmailList').then(module => ({ default: module.AdminEmailList })));
const AdminBlogQueue = lazy(() => import('./components/AdminBlogQueue'));
const BroadcastNotificationBanner = lazy(() => import('./components/BroadcastNotificationBanner'));
const SessionMonitor = lazy(() => import('./components/SessionMonitor'));
const FirstRunTourManager = lazy(() => import('./components/FirstRunTourManager').then(m => ({ default: m.FirstRunTourManager })));
const PageTourManager = lazy(() => import('./components/PageTourManager').then(m => ({ default: m.PageTourManager })));
const BetaOfferPopup = lazy(() => import('./components/BetaOfferPopup').then(m => ({ default: m.BetaOfferPopup })));
const LoginScreenSupabase = lazy(() => import('./components/LoginScreenSupabase').then(module => ({ default: module.LoginScreenSupabase })));
const LandingPage = lazy(() => import('./components/LandingPage/LandingPage').then(module => ({ default: module.LandingPage })));
const BlogPage = lazy(() => import('./components/Blog/BlogPage'));
const BlogPostPage = lazy(() => import('./components/Blog/BlogPostPage'));
const PublicPositionCalculatorPage = lazy(() => import('./components/PublicPositionCalculatorPage'));
const RiskRewardCalculatorPage = lazy(() => import('./components/PublicTradingToolPages').then(module => ({ default: module.RiskRewardCalculatorPage })));
const DrawdownRecoveryCalculatorPage = lazy(() => import('./components/PublicTradingToolPages').then(module => ({ default: module.DrawdownRecoveryCalculatorPage })));
const PropFirmRiskCalculatorPage = lazy(() => import('./components/PublicTradingToolPages').then(module => ({ default: module.PropFirmRiskCalculatorPage })));
const MonteCarloSimulatorPage = lazy(() => import('./components/PublicTradingToolPages').then(module => ({ default: module.MonteCarloSimulatorPage })));
const TradingJournalTemplatePage = lazy(() => import('./components/PublicTradingToolPages').then(module => ({ default: module.TradingJournalTemplatePage })));
const TradingJournalFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.TradingJournalFeaturePage })));
const ForexTradingJournalFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.ForexTradingJournalFeaturePage })));
const CTraderJournalFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.CTraderJournalFeaturePage })));
const Mt5JournalFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.Mt5JournalFeaturePage })));
const Mt4JournalFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.Mt4JournalFeaturePage })));
const TradingDiaryFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.TradingDiaryFeaturePage })));
const TradingJournalDashboardFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.TradingJournalDashboardFeaturePage })));
const AiTradingChatFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.AiTradingChatFeaturePage })));
const AiTradingReportFeaturePage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.AiTradingReportFeaturePage })));
const TradingToolsHubPage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.TradingToolsHubPage })));
const PublicNotFoundPage = lazy(() => import('./components/PublicFeaturePages').then(module => ({ default: module.PublicNotFoundPage })));
const LegalPage = lazy(() => import('./components/LegalPage'));
// Where the unsubscribe link in an email lands. Public by necessity: someone
// leaving a mailing list will not sign in first.
const UnsubscribedPage = lazy(() => import('./components/UnsubscribedPage'));
const DesignSystemMockups = lazy(() => import('./components/DesignSystemMockups'));
const AuthCallbackPage = lazy(() => import('./components/AuthCallbackPage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const DemoBanner = lazy(() => import('./components/DemoBanner'));
const DataLoadErrorBanner = lazy(() => import('./components/DataLoadErrorBanner'));

/**
 * Whether this browser is holding a Supabase session, read synchronously.
 *
 * Used to decide whether the first paint should wait for the auth check. The
 * check itself is async, so without this either signed-in users get a flash of
 * the landing page — and its blog request ahead of their own data — or every
 * anonymous visitor waits on a spinner before the marketing page. The token
 * lives in localStorage under `sb-<project-ref>-auth-token`.
 */
const hasStoredSession = (): boolean => {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.includes('auth-token') && localStorage.getItem(key)) {
        return true;
      }
    }
  } catch {
    // Private mode or storage disabled: treat as signed out and render the
    // public tree rather than hanging on a spinner.
  }
  return false;
};

// 🔄 Loading Fallback per Lazy Components
/**
 * The app's own pages, which only exist once you are logged in.
 *
 * Someone who is logged out and asks for one of these used to fall through to
 * `path="*"` and be told the page does not exist. That is both untrue and the
 * point at which a person following a link from an email, or an old bookmark,
 * gives up — they came to the right address and were told they had the wrong
 * one. Asking them to log in is the honest answer.
 */
const APP_ONLY_PATHS = [
  '/dashboard',
  '/charts',
  '/data',
  '/diary',
  '/platforms',
  '/report',
  '/settings',
  '/tajir',
];

const LoadingFallback: React.FC = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#000000'
  }}>
    <div style={{
      textAlign: 'center',
      padding: '2rem'
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        border: '4px solid #1f1f1f',
        borderTopColor: '#ffffff',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 1rem'
      }} />
      <p style={{ color: '#9ca3af', fontSize: '14px' }}>Caricamento...</p>
    </div>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const AdminProtectedRoute: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const [status, setStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    let isMounted = true;

    const validateAdminAccess = async () => {
      const valid = await AdminAuthService.validateSession();
      if (isMounted) {
        setStatus(valid ? 'allowed' : 'denied');
      }
    };

    validateAdminAccess();

    return () => {
      isMounted = false;
    };
  }, []);

  if (status === 'checking') {
    return <LoadingFallback />;
  }

  if (status === 'denied') {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

const AnalyticsRouteTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const path = `${location.pathname || '/'}${location.search || ''}`;
    GoogleAnalyticsService.trackPageView(path, document.title || undefined);
    PostHogService.trackPageView(path, document.title || undefined);
  }, [location.pathname, location.search]);

  return null;
};

// === Page Tracker Component REMOVED ===
// Removed PageTracker - only tracking logins/registrations now

// === UIStateManager Component ===
// Tracks route changes and restores UI state on mount
const UIStateManager: React.FC<{
  currentUser: string | null;
  onStateRestored?: (activeFileId: string | null) => void;
}> = ({ currentUser, onStateRestored }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasRestoredState, setHasRestoredState] = React.useState(false);

  // Ripristina lo stato al primo mount
  useEffect(() => {
    if (!hasRestoredState && currentUser) {
      const savedState = UIStateService.loadState(currentUser);

      // Ripristina l'ultima pagina solo se l'app è stata aperta dalla home.
      // Chi arriva su un indirizzo preciso deve restarci: il ritorno da
      // cTrader porta un codice monouso, il link di un'email o di una
      // notifica porta dove è stato cliccato — spostarlo sull'ultima pagina
      // visitata butta via l'uno e l'altro.
      const isSpecial = (p: string) =>
        p.includes('/login') || p.includes('/auth/') || p.includes('/reset-password');
      const currentPath = location.pathname;
      const savedPath = savedState.currentPath;

      if (currentPath === '/' &&
          savedPath &&
          savedPath !== '/' &&
          !isSpecial(savedPath)) {

        // Ripristino route salvata
        const fullPath = savedState.searchParams
          ? `${savedPath}${savedState.searchParams}`
          : savedPath;
        navigate(fullPath, { replace: true });
      }

      // Notifica il componente padre dell'active file salvato
      if (onStateRestored && savedState.activeFileId) {
        onStateRestored(savedState.activeFileId);
      }

      // Ripristina la posizione di scroll
      UIStateService.restoreScrollPosition(currentUser);

      setHasRestoredState(true);
    }
  }, [hasRestoredState, currentUser, navigate, location.pathname, onStateRestored]);

  // Traccia i cambiamenti di route
  useEffect(() => {
    if (hasRestoredState && currentUser) {
      const path = location.pathname;
      const search = location.search;

      // Salva solo le route dell'applicazione principale (non login, landing, ecc.)
      if (path !== '/login' && !path.includes('/auth/')) {
        UIStateService.updatePath(path, search, currentUser);
      }
    }
  }, [location.pathname, location.search, hasRestoredState, currentUser]);

  // Inizializza il tracking dello scroll
  useEffect(() => {
    if (hasRestoredState && currentUser) {
      const cleanup = UIStateService.initScrollTracking(currentUser);
      return cleanup;
    }
  }, [hasRestoredState, currentUser]);

  return null; // Questo componente non renderizza nulla
};

// === UIStateManagerWrapper ===
// Wrapper che connette UIStateManager con DataFilesContext
const UIStateManagerWrapper: React.FC<{
  currentUser: string | null;
}> = ({ currentUser }) => {
  const { setActiveFile, standardFiles, isLoading } = useDataFiles();

  const handleStateRestored = React.useCallback((activeFileId: string | null) => {
    // Aspetta che i file siano caricati prima di ripristinare l'active file
    if (!isLoading && standardFiles.length > 0 && activeFileId) {
      // Verifica che il file esista ancora
      const fileExists = standardFiles.some(f => f.id === activeFileId);
      if (fileExists) {
        // Ripristino active file
        setActiveFile(activeFileId);
      } else {
        console.warn('⚠️ Active file salvato non più disponibile:', activeFileId);
      }
    }
  }, [setActiveFile, standardFiles, isLoading]);

  // Effetto per ripristinare il file quando i dati sono pronti.
  //
  // Restores once per signed-in user, not on every change to the file count.
  // It depended on `standardFiles.length`, so importing a file re-ran it: the
  // import selected the new file, the list grew, this fired again and put the
  // previously active file back. The import had worked, and the Journal sat
  // there showing the old file as though nothing had happened.
  const restoredForUser = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading || !currentUser || standardFiles.length === 0) return;
    if (restoredForUser.current === currentUser) return;
    restoredForUser.current = currentUser;

    const savedState = UIStateService.loadState(currentUser);
    if (savedState.activeFileId) {
      handleStateRestored(savedState.activeFileId);
    }
  }, [isLoading, standardFiles.length, currentUser, handleStateRestored]);

  return <UIStateManager currentUser={currentUser} onStateRestored={handleStateRestored} />;
};

const AuthenticatedRouteAnalyticsTracker: React.FC<{
  currentUserId: string | null;
}> = ({ currentUserId }) => {
  const location = useLocation();

  useEffect(() => {
    if (!currentUserId) return;

    const pagePath = location.pathname || '/';
    const pageTitle = AnalyticsService.getPageTitle(pagePath);
    const pageKey = pagePath.replace(/^\//, '') || 'dashboard';

    AnalyticsService.trackPageEnter(currentUserId, pagePath, pageTitle);

    UnifiedAnalyticsService.trackProductPageViewed({
      userId: currentUserId,
      pagePath,
      pageKey,
      source: 'app_navigation'
    });

    const heartbeat = window.setInterval(() => {
      AnalyticsService.updatePageTime();
    }, 30000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        AnalyticsService.updatePageTime();
      }
    };

    const handlePageHide = () => {
      AnalyticsService.trackPageExit();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      AnalyticsService.trackPageExit();
    };
  }, [currentUserId, location.pathname]);

  return null;
};

// === Componente Navigation che usa le classi CSS ===
const Navigation: React.FC<{
  onLogout: () => void;
  currentUser: string | null;
}> = ({ onLogout, currentUser }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const [feedbackType, setFeedbackType] = React.useState<'bug' | 'idea' | 'confusion' | 'other'>('bug');
  const [feedbackMessage, setFeedbackMessage] = React.useState('');
  const [feedbackSending, setFeedbackSending] = React.useState(false);
  const [feedbackSent, setFeedbackSent] = React.useState(false);
  const [currentPlan, setCurrentPlan] = React.useState<SubscriptionTier>('free');

  React.useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      const user = await SupabaseAuthService.getCurrentUser();
      if (!isMounted || !user) {
        if (isMounted) setCurrentPlan('free');
        return;
      }

      const email = (user.email || '').toLowerCase();
      setCurrentPlan(INTERNAL_ADMIN_EMAILS.includes(email) ? 'advanced' : extractUserTajirPlan(user));
    };

    loadPlan();
    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  const planBadgeLabel = currentPlan === 'advanced'
    ? 'Coming soon'
    : currentPlan === 'free'
      ? 'Free'
      : currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);

  const handleFeedbackSubmit = async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    try {
      const { FeedbackService } = await import('./services/FeedbackService');
      const result = await FeedbackService.submitFeedback({
        feedbackType,
        message: feedbackMessage.trim(),
        pageUrl: window.location.href,
      });
      if (result.success) {
        setFeedbackSent(true);
        setTimeout(() => {
          setShowFeedbackModal(false);
          setFeedbackSent(false);
          setFeedbackMessage('');
          setFeedbackType('bug');
        }, 2000);
      }
    } catch (e) {
      console.error('Feedback error:', e);
    } finally {
      setFeedbackSending(false);
    }
  };

  // Close mobile menu on route change
  React.useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  // iOS-safe scroll lock: overflow:hidden alone doesn't stop Safari rubber-banding
  React.useEffect(() => {
    if (showMobileMenu) {
      const scrollY = window.scrollY;
      document.body.dataset.drawerScrollY = String(scrollY);
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = parseInt(document.body.dataset.drawerScrollY || '0', 10);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) window.scrollTo(0, scrollY);
      delete document.body.dataset.drawerScrollY;
    }
    return () => {
      // Cleanup on unmount - always restore scroll
      const scrollY = parseInt(document.body.dataset.drawerScrollY || '0', 10);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) window.scrollTo(0, scrollY);
    };
  }, [showMobileMenu]);

  // Close drawer if window resizes to desktop (e.g. phone rotation to landscape)
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowMobileMenu(false);
        setShowUserMenu(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Escape key closes both drawers
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMobileMenu(false);
        setShowUserMenu(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navItems = [
    { path: '/', label: t('navigation.dashboard'), icon: <LayoutDashboard size={18} /> },
    { path: '/tajir', label: t('navigation.tajirAI'), icon: <BrainCircuit size={18} /> },
    { path: '/data', label: t('navigation.myTrades'), icon: <Database size={18} /> },
    { path: '/diary', label: t('navigation.diary'), icon: <BookText size={18} /> },
    { path: '/report', label: t('navigation.report'), icon: <FileText size={18} /> }
  ];

  // Connected platforms live here rather than in the top bar: at 1280 the
  // header row is already full (see App.css), and this is a page visited to
  // set up once and check on, not every session.
  const userMenuItems = [
    { path: '/platforms', label: t('navigation.platforms', 'Connected platforms'), icon: <Plug size={16} /> },
    { path: '/settings?tab=preferences', label: t('navigation.settings'), icon: <Settings size={16} /> },
    { path: '/settings?tab=notifications', label: t('navigation.notifications'), icon: <User size={16} /> },
    { path: '/settings?tab=feedback', label: t('navigation.feedback'), icon: <MessageSquare size={16} /> },
    { path: '/settings?tab=donations', label: t('navigation.donations'), icon: <Heart size={16} /> }
  ];

  return (
    <>
      <nav className="app-nav" style={{
          background: 'var(--header-bg, #000000)',
          height: 'calc(64px + env(safe-area-inset-top, 0px))',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingLeft: '2rem',
          paddingRight: '2rem',
          boxShadow: 'var(--shadow-md)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--header-border)',
          // Force GPU compositing: prevents Safari iOS fixed-positioning glitch
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          willChange: 'transform',
        }}
        // position:fixed already acts as containing block for absolute children
      >
        {/* Left cluster: logo + global data-file selector */}
        <div className="app-nav-left" style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
          {/* Logo */}
          <div style={{
            color: 'white',
            fontSize: '1.25rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <img
              className="app-logo-img"
              src="/logo.svg"
              alt="TradingSpace"
              style={{
                width: '52px',
                height: '52px',
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 12px rgba(56, 189, 248, 0.26))',
                flexShrink: 0
              }}
            />
            <span className="app-brand-text">TradingSpace</span>
          </div>

          {/* Global file selector — the whole platform follows this file */}
          <GlobalFileSelector />
        </div>

        {/* Desktop nav items - hidden on mobile via CSS (.app-nav-desktop class) */}
        <div className="app-nav-desktop" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}

          {/* Segnala Bug Button */}
          <button
            onClick={() => setShowFeedbackModal(true)}
            className="nav-link"
            style={{
              marginLeft: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(239,68,68,0.35)',
              color: '#f87171',
              background: 'transparent',
              cursor: 'pointer',
            }}
            title="Report a problem"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.6)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.35)'; }}
          >
            <Flag size={15} />
            {/* "Feedback", not "Report" — the nav already has a Report page. */}
            <span style={{ fontSize: '13px', fontWeight: '600' }}>Feedback</span>
          </button>

          {/* Feedback Modal */}
          {showFeedbackModal && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.62)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'calc(84px + env(safe-area-inset-top, 0px)) 1rem 1.25rem',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                boxSizing: 'border-box',
              }}
              onClick={(e) => { if (e.target === e.currentTarget) setShowFeedbackModal(false); }}
            >
              <div style={{
                background: '#1e2130',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                padding: '1.5rem',
                width: '100%',
                maxWidth: '440px',
                maxHeight: 'calc(100dvh - 110px)',
                overflowY: 'auto',
                boxSizing: 'border-box',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                margin: 'auto',
              }}>
                {feedbackSent ? (
                  <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
                    <div style={{ color: '#10b981', fontWeight: '700', fontSize: '1.1rem' }}>Report sent!</div>
                    <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>Thanks, we'll review it soon.</div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Flag size={18} color="#f87171" />
                        <span style={{ fontWeight: '700', fontSize: '1rem', color: '#f1f5f9' }}>Report a problem</span>
                      </div>
                      <button onClick={() => setShowFeedbackModal(false)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '4px' }}>
                        <X size={18} />
                      </button>
                    </div>

                    {/* Type selector */}
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      {([
                        { value: 'bug', label: 'Bug', color: '#ef4444' },
                        { value: 'idea', label: 'Idea', color: '#3b82f6' },
                        { value: 'confusion', label: 'Confusing', color: '#f59e0b' },
                        { value: 'other', label: 'Other', color: '#8b5cf6' },
                      ] as const).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setFeedbackType(opt.value)}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            border: `1px solid ${feedbackType === opt.value ? opt.color : 'rgba(255,255,255,0.1)'}`,
                            background: feedbackType === opt.value ? `${opt.color}22` : 'transparent',
                            color: feedbackType === opt.value ? opt.color : '#9ca3af',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            transition: 'all 0.15s',
                          }}
                        >{opt.label}</button>
                      ))}
                    </div>

                    {/* Message */}
                    <textarea
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder="Describe the problem or idea..."
                      rows={4}
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        color: '#f1f5f9',
                        fontSize: '0.875rem',
                        resize: 'vertical',
                        outline: 'none',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        lineHeight: '1.5',
                      }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                    />

                    <button
                      onClick={handleFeedbackSubmit}
                      disabled={!feedbackMessage.trim() || feedbackSending}
                      style={{
                        marginTop: '0.75rem',
                        width: '100%',
                        padding: '0.625rem',
                        borderRadius: '8px',
                        border: 'none',
                        background: feedbackMessage.trim() ? '#6366f1' : 'rgba(99,102,241,0.3)',
                        color: '#fff',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        cursor: feedbackMessage.trim() ? 'pointer' : 'not-allowed',
                        transition: 'background 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      {feedbackSending ? (
                        <>
                          <div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                          Sending...
                        </>
                      ) : 'Send report'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* One click to Base, for the accounts that do not have it.
              Constant rather than tucked into a menu: the upsell that converts
              is the one visible at the moment somebody decides, and every limit
              message in the product already assumes the plan is on screen.
              Hidden the instant they upgrade — selling Base to a Base customer
              is how an upsell starts reading as spam. */}
          {currentPlan === 'free' && (
            <button
              type="button"
              data-testid="header-upgrade"
              onClick={async () => {
                const result = await StripeCheckoutService.createSession('base');
                if (result.ok && result.url) window.location.href = result.url;
                else navigate('/settings?tab=billing');
              }}
              className="nav-link app-upgrade-cta"
              style={{
                marginLeft: '0.75rem',
                padding: '7px 14px',
                borderRadius: '999px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '13px',
                color: '#fff',
                background: 'linear-gradient(135deg, #4f6bff, #8b5cf6)',
                whiteSpace: 'nowrap',
              }}
            >
              Upgrade
            </button>
          )}

          {/* Desktop User Menu */}
          <div style={{
            marginLeft: '1rem',
            paddingLeft: '1rem',
            borderLeft: '1px solid rgba(255,255,255,0.2)',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="nav-link"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <User size={18} />
              <span className="app-user-email" style={{ fontSize: '14px' }}>{currentUser}</span>
              {/* Beside the account name rather than beside the logo, where it
                  used to sit: this is a fact about the account, and it is the
                  answer every limit message in the product now assumes is
                  already on screen. */}
              <span className={`app-plan-badge app-plan-badge-${currentPlan}`}>{planBadgeLabel}</span>
              <ChevronDown size={16} style={{
                transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} />
            </button>

            {showUserMenu && (
              <>
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                  onClick={() => setShowUserMenu(false)}
                />
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: 'var(--header-bg, #000000)',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: 'var(--radius-md)',
                  minWidth: '200px',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 1000,
                  overflow: 'hidden'
                }}>
                  {userMenuItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setShowUserMenu(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        color: 'var(--color-text-primary)',
                        textDecoration: 'none',
                        transition: 'background 0.2s',
                        borderBottom: '1px solid var(--color-border-light)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                  <button
                    onClick={() => { setShowUserMenu(false); onLogout(); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 16px', background: 'none', border: 'none',
                      color: '#ef4444', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={16} />
                    <span>{t('navigation.logout')}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile Hamburger Button - shown only on mobile via CSS */}
        <button
          className="mobile-hamburger-button"
          onClick={() => setShowMobileMenu(true)}
          aria-label="Open navigation menu"
          aria-expanded={showMobileMenu}
          aria-controls="mobile-drawer"
        >
          <Menu size={24} />
        </button>

        {/* Mobile center title - shown only on ≤480px when logo text is hidden */}
        <span className="app-nav-mobile-title">TradingSpace</span>
      </nav>

      {/* Mobile Drawer Overlay */}
      {showMobileMenu && (
        <div
          className="mobile-drawer-overlay"
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div
        id="mobile-drawer"
        className={`mobile-drawer ${showMobileMenu ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu di navigazione"
      >
        <div className="mobile-drawer-header">
          <span style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>Menu</span>
          <button className="mobile-drawer-close" onClick={() => setShowMobileMenu(false)}>
            <X size={20} />
          </button>
        </div>

        <div className="mobile-drawer-nav">
          {/* Navigation Section */}
          <div className="mobile-drawer-section">
            <p className="mobile-drawer-section-title">Navigation</p>
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-drawer-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => setShowMobileMenu(false)}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Account Section */}
          <div className="mobile-drawer-section">
            <p className="mobile-drawer-section-title">Account</p>
            {userMenuItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className="mobile-drawer-item"
                onClick={() => setShowMobileMenu(false)}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Segnala + Discord + Logout */}
          <div className="mobile-drawer-section">
            <button
              className="mobile-drawer-item"
              onClick={() => { setShowMobileMenu(false); setShowFeedbackModal(true); }}
              style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
            >
              <Flag size={18} />
              <span>{t('navigation.reportProblem', 'Report a problem')}</span>
            </button>
            <a
              href={COMMUNITY_DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-drawer-item"
              onClick={() => setShowMobileMenu(false)}
            >
              <svg width="18" height="18" viewBox="0 0 71 55" fill="currentColor">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
              </svg>
              <span>{t('navigation.communityDiscord', 'Community Discord')}</span>
            </a>
            <button
              className="mobile-drawer-item logout"
              onClick={() => { setShowMobileMenu(false); onLogout(); }}
            >
              <LogOut size={18} />
              <span>{t('navigation.logout')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Nav Bar */}
      <nav className="mobile-bottom-nav">
        {navItems.map(item => (
          <Link
            key={item.path}
            to={item.path}
            className={`mobile-bottom-nav-item ${location.pathname === item.path ? 'active' : ''}`}
          >
            {item.icon}
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
    </>
  );
};

// Check if current path is admin route
const isAdminRoute = () => {
  return window.location.pathname.startsWith('/admin');
};

function App() {
  useGlobalScroll();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Read once at mount so a recovery session that survived a reload is caught
  // before any of the app is built, and kept in state so setting the password
  // swaps the tree back without a manual refresh. `storage` covers the other
  // tab: finishing the reset in one should not leave a second one holding an
  // open app.
  const [resetPending, setResetPending] = useState(() => PasswordResetGuard.isPending());
  useEffect(() => {
    const sync = () => setResetPending(PasswordResetGuard.isPending());
    window.addEventListener(PASSWORD_RESET_PENDING_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PASSWORD_RESET_PENDING_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);

  /**
   * Nothing here blocks the app any more. The questionnaire and the welcome
   * tutorial both used to gate on `tutorial_shown`; with them gone the only
   * remaining onboarding is the anchored tour, which manages its own flag in
   * FirstRunTourManager. This is left as the place the donation reminder is
   * armed for a user who has not finished onboarding.
   */
  const checkOnboardingStatus = async (userId: string) => {
    try {
      const progress = await TutorialService.getProgress(userId);
      const onboarded = progress.some(s => s.step_name === 'tutorial_shown' && s.completed);

      if (!onboarded) {
        // 30-second delay, so it is not the first thing a new account meets.
        DonationReminderService.initializeReminderCheck(userId);
      }

      setIsCheckingOnboarding(false);
    } catch (error: any) {
      console.error('Error checking onboarding status:', error.message);
      setIsCheckingOnboarding(false);
    }
  };

  // Check authentication status on mount with Supabase
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Skip user auth check for admin routes
        if (isAdminRoute()) {
          setIsCheckingOnboarding(false);
          return;
        }

        if (!isSupabaseConfigured()) {
          console.error('⚠️ Supabase not configured! Check .env.local');
          setIsLoggedIn(false);
          setCurrentUser(null);
          setCurrentUserId(null);
          setIsCheckingOnboarding(false);
          return;
        }

        const loggedIn = await SupabaseAuthService.isLoggedIn();
        const user = loggedIn ? await SupabaseAuthService.getCurrentUser() : null;

        setIsLoggedIn(loggedIn);
        setCurrentUser(user?.email || null);
        setCurrentUserId(user?.id || null);
        // Supabase auth checked

        // Start session tracking if logged in
        if (loggedIn && user) {
          // 🔧 FIX: Wrap service calls in try-catch so checkOnboardingStatus ALWAYS runs
          try {
            // Nothing called ErrorLoggerService.init(), so the service sat inert:
            // every report carried a null user, and — because init() is also
            // what installs the window error and unhandledrejection handlers —
            // uncaught errors were never captured at all. With no error boundary
            // either, a crash in production left no trace anywhere.
            ErrorLoggerService.init(user.id, user.email || '');

            await SessionService.startSession(user.id, user.email || '');
            SessionCleanupService.startAutomaticCleanup();
            // ❌ REMOVED: AnalyticsService.initPageTracking(user.id) - only tracking logins/registrations now
          } catch (serviceError) {
            console.error('⚠️ Non-critical service error during startup:', serviceError);
            // Continue anyway - these are non-critical services
          }

          // Check onboarding status - MUST always run regardless of above errors
          await checkOnboardingStatus(user.id);
        } else {
          setIsCheckingOnboarding(false);
        }
      } catch (error) {
        console.error('⚠️ Auth bootstrap failed, falling back to public routes:', error);
        setIsLoggedIn(false);
        setCurrentUser(null);
        setCurrentUserId(null);
        setIsCheckingOnboarding(false);
      }
    };

    checkAuth();

    // Listen to auth changes (only for non-admin routes)
    if (!isAdminRoute()) {
      const unsubscribe = SupabaseAuthService.onAuthStateChange(async (user, event) => {
        // The recovery session is caught here rather than only in
        // AuthCallbackPage, because that page reads `?type=recovery` from the
        // query string and the token does not always arrive that way: with
        // `detectSessionInUrl` the SDK can consume a `#access_token=…` fragment
        // and sign the user in before the callback route even mounts. Then the
        // flag was never set and the app opened normally — which is the whole
        // hole this was supposed to close. Supabase fires PASSWORD_RECOVERY in
        // every one of those paths.
        if (event === 'PASSWORD_RECOVERY') {
          PasswordResetGuard.mark();
        }

        // 🔧 FIX: Do NOT re-run onboarding check here.
        // checkAuth() handles the initial load; handleLoginSuccess() handles explicit logins.
        // Re-running checkOnboardingStatus here would unmount the entire Router on every
        // TOKEN_REFRESHED event (e.g. when the user switches windows), causing navigation reset.
        if (!user) {
          // User logged out - reset state
          setIsCheckingOnboarding(false);
        }

        setIsLoggedIn(!!user);
        setCurrentUser(user?.email || null);
        setCurrentUserId(user?.id || null);

        // 🔧 FIX: Don't start session here - it's handled by checkAuth/handleLoginSuccess
        // This prevents duplicate session creation
        // Auth state changed
      });

      return () => {
        unsubscribe();
      };
    }
  }, []);

  const handleLoginSuccess = async (username: string, flow: 'login' | 'signup' = 'login') => {
    // Login successful. After the Sign in button only — not after registering,
    // and not when a stored session opens the app — a few seconds of quote
    // over the journal loading behind it. The login screen has already
    // fetched this chunk and its font, so it appears at once.
    if (flow === 'login') {
      import('./components/LoginQuoteOverlay').then((m) => m.showLoginQuote()).catch(() => undefined);
    }

    // Set loading state FIRST to prevent dashboard flash
    setIsCheckingOnboarding(true);
    setIsLoggedIn(true);
    setCurrentUser(username);

    try {
      // 🔧 FIX: Supabase session may not be fully ready immediately after
      // registration → getCurrentUser() can return null with AuthSessionMissingError.
      // Retry up to 3 times with 300ms delay to wait for session establishment.
      let user = await SupabaseAuthService.getCurrentUser();
      let attempts = 0;
      while (!user && attempts < 3) {
        await new Promise(resolve => setTimeout(resolve, 300));
        user = await SupabaseAuthService.getCurrentUser();
        attempts++;
        if (!user) console.warn(`⚠️ Retrying getCurrentUser (attempt ${attempts}/3)...`);
      }

      if (user && user.id && user.email) {
        setCurrentUserId(user.id);

        try {
          await SessionService.startSession(user.id, user.email);
          // Session tracking started
        } catch (sessionError) {
          console.error('⚠️ Session tracking error (non-critical):', sessionError);
        }

        const visitorId = UnifiedAnalyticsService.getVisitorId();

        if (flow === 'signup') {
          await UnifiedAnalyticsService.trackAuthEvent('signup_completed', {
            userId: user.id,
            visitorId,
            source: 'auth_register_form',
            pagePath: '/login'
          });
          await AnalyticsService.trackLandingConversion(visitorId, user.id);
        } else {
          await UnifiedAnalyticsService.trackAuthEvent('login_completed', {
            userId: user.id,
            visitorId,
            source: 'auth_login_form',
            pagePath: '/login'
          });
        }

        await checkOnboardingStatus(user.id);
      } else {
        // All retries failed — still unblock UI so user doesn't see infinite loading
        console.error('❌ Could not get user after retries, proceeding without onboarding check');
        setIsCheckingOnboarding(false);
      }
    } catch (error) {
      console.error('Error in handleLoginSuccess:', error);
      setIsCheckingOnboarding(false);
    }
  };

  const handleLogout = async () => {
    try {
      UnifiedAnalyticsService.reset();
      // Drop the user context first, so anything that fails during teardown is
      // not filed against the account that is on its way out.
      ErrorLoggerService.clear();
      // End session tracking
      await SessionService.endSession();

      // Logout and clear all cache
      await SupabaseAuthService.logout();

      // Logged out

      // 🔄 CRITICAL: Full page reload to clear ALL context and state
      // This prevents data leakage between different user accounts
      window.location.href = '/';
    } catch (error) {
      console.error('Error during logout:', error);
      // Force reload anyway to clear state
      window.location.href = '/';
    }
  };

  useEffect(() => {
    UnifiedAnalyticsService.bootstrap();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      UnifiedAnalyticsService.identifyUser(currentUserId, currentUser ? { email: currentUser } : undefined);
    }
  }, [currentUserId, currentUser]);

  // DEV-ONLY: widget redesign preview harness (real Dashboard + demo data, no auth)
  if (process.env.NODE_ENV === 'development' && window.location.pathname === '/widget-lab') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <DashboardLab />
      </Suspense>
    );
  }

  // DEV-ONLY: onboarding preview harness. Same placement as /widget-lab, ahead
  // of the auth gate — the onboarding only shows itself to a brand-new account,
  // so there is otherwise no way to look at it twice.
  if (process.env.NODE_ENV === 'development' && window.location.pathname === '/onboarding-lab') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <OnboardingLab />
      </Suspense>
    );
  }

  // Admin routes bypass user authentication
  if (isAdminRoute()) {
    return (
      <DialogProvider>
        <ToastProvider>
          <ToastContainer />
          <Router>
            <div className="App">
              <Seo
                title="TradingSpace Admin"
                description="Private TradingSpace admin area."
                path="/admin"
                noIndex
              />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/admin" element={<AdminLoginPage />} />
                  <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboardFunctional /></AdminProtectedRoute>} />
                  <Route path="/admin/operations" element={<AdminProtectedRoute><AdminMonitoring /></AdminProtectedRoute>} />
                  <Route path="/admin/content" element={<AdminProtectedRoute><AdminBlogQueue /></AdminProtectedRoute>} />
                  <Route path="/admin/audience" element={<Navigate to="/admin/audience/landing" replace />} />
                  <Route path="/admin/audience/landing" element={<AdminProtectedRoute><AdminLandingAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/audience/platform" element={<AdminProtectedRoute><AdminPlatformAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/audience/blog" element={<AdminProtectedRoute><AdminBlogAnalytics /></AdminProtectedRoute>} />
                  <Route path="/admin/audience/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
                  {/* Client management. Reachable only by typing the path — no
                      link points here — and the data behind it is gated by the
                      admin-users edge function, not by this route guard. */}
                  <Route path="/admin/clients" element={<AdminProtectedRoute><AdminClients /></AdminProtectedRoute>} />
                  <Route path="/admin/revenue" element={<AdminProtectedRoute><AdminRevenue /></AdminProtectedRoute>} />
                  <Route path="/admin/ai-costs" element={<AdminProtectedRoute><AdminAiCosts /></AdminProtectedRoute>} />
                  <Route path="/admin/monitoring" element={<AdminProtectedRoute><AdminMonitoring /></AdminProtectedRoute>} />
                  <Route path="/admin/feedback" element={<AdminProtectedRoute><FeedbackCenter /></AdminProtectedRoute>} />
                  <Route path="/admin/emails" element={<AdminProtectedRoute><AdminEmailList /></AdminProtectedRoute>} />
                  <Route path="/admin/contact" element={<AdminProtectedRoute><AdminContactMessages /></AdminProtectedRoute>} />
                  <Route path="/admin/users" element={<Navigate to="/admin/audience/users" replace />} />
                  <Route path="/admin/blog-queue" element={<AdminProtectedRoute><AdminBlogQueue /></AdminProtectedRoute>} />
                  <Route path="/admin/broadcast" element={<AdminProtectedRoute><AdminBroadcast /></AdminProtectedRoute>} />
                  <Route path="/admin/sessions" element={<AdminProtectedRoute><SessionMonitor /></AdminProtectedRoute>} />
                </Routes>
              </Suspense>
            </div>
          </Router>
        </ToastProvider>
      </DialogProvider>
    );
  }

  const isPublicContentRoute = (() => {
    const path = window.location.pathname;
    return path === '/legal' || path === '/blog' || path === '/design-system' || path === '/position-size-calculator' ||
      path === '/risk-reward-calculator' || path === '/drawdown-recovery-calculator' ||
      path === '/prop-firm-risk-calculator' || path === '/monte-carlo-trading-simulator' ||
      path === '/trading-journal-template' || path === '/trading-journal' ||
      path === '/free-trading-journal' ||
      path === '/forex-trading-journal' || path === '/trading-diary' ||
      path === '/ctrader-trading-journal' || path === '/mt5-trading-journal' || path === '/mt4-trading-journal' ||
      path === '/trading-analytics' || path === '/trading-journal-dashboard' ||
      path === '/ai-trading-chat' || path === '/ai-trading-report' || path === '/trading-tools' ||
      path.startsWith('/blog/');
  })();

  // Public content routes stay public even when the user is authenticated.
  // Otherwise /blog and /legal get trapped inside the authenticated app shell.
  if (isPublicContentRoute) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Router>
          <AnalyticsConsentBanner />
          <AnalyticsRouteTracker />
          <Routes>
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/unsubscribed" element={<UnsubscribedPage />} />
            <Route path="/design-system" element={<DesignSystemMockups />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/position-size-calculator" element={<PublicPositionCalculatorPage />} />
            <Route path="/risk-reward-calculator" element={<RiskRewardCalculatorPage />} />
            <Route path="/drawdown-recovery-calculator" element={<DrawdownRecoveryCalculatorPage />} />
            <Route path="/prop-firm-risk-calculator" element={<PropFirmRiskCalculatorPage />} />
            <Route path="/monte-carlo-trading-simulator" element={<MonteCarloSimulatorPage />} />
            <Route path="/trading-journal-template" element={<TradingJournalTemplatePage />} />
            <Route path="/trading-journal" element={<TradingJournalFeaturePage />} />
            <Route path="/forex-trading-journal" element={<ForexTradingJournalFeaturePage />} />
            <Route path="/ctrader-trading-journal" element={<CTraderJournalFeaturePage />} />
            <Route path="/mt5-trading-journal" element={<Mt5JournalFeaturePage />} />
            <Route path="/mt4-trading-journal" element={<Mt4JournalFeaturePage />} />
            <Route path="/free-trading-journal" element={<Navigate to="/trading-journal" replace />} />
            <Route path="/trading-diary" element={<TradingDiaryFeaturePage />} />
            <Route path="/trading-analytics" element={<Navigate to="/trading-journal-dashboard" replace />} />
            <Route path="/trading-journal-dashboard" element={<TradingJournalDashboardFeaturePage />} />
            <Route path="/ai-trading-chat" element={<AiTradingChatFeaturePage />} />
            <Route path="/ai-trading-report" element={<AiTradingReportFeaturePage />} />
            <Route path="/trading-tools" element={<TradingToolsHubPage />} />
          </Routes>
        </Router>
      </Suspense>
    );
  }

  // Wait for the auth check before choosing a tree — but only for someone who
  // might be signed in.
  //
  // This test used to sit *below* the `!isLoggedIn` branch, and `isLoggedIn`
  // starts false, so every reload by a signed-in user mounted the public
  // landing page first. That is not just a flash of the wrong page: the landing
  // fetches blog_posts on mount, which put the blog ahead of the user's own
  // trades in the startup queue — trade data did not begin loading until
  // roughly 3.7s into a 10s wait.
  //
  // Waiting unconditionally would fix that by making every anonymous visitor
  // stare at a spinner before the marketing page, which is the wrong trade for
  // the one page that has to be fast. A stored Supabase session is a synchronous
  // localStorage read and tells us which case this is.
  if (isCheckingOnboarding && hasStoredSession()) {
    return <LoadingFallback />;
  }

  // Show public routes (landing + login) if not authenticated
  if (!isLoggedIn) {
    return (
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <AnalyticsConsentBanner />
          <AnalyticsRouteTracker />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/position-size-calculator" element={<PublicPositionCalculatorPage />} />
            <Route path="/risk-reward-calculator" element={<RiskRewardCalculatorPage />} />
            <Route path="/drawdown-recovery-calculator" element={<DrawdownRecoveryCalculatorPage />} />
            <Route path="/prop-firm-risk-calculator" element={<PropFirmRiskCalculatorPage />} />
            <Route path="/monte-carlo-trading-simulator" element={<MonteCarloSimulatorPage />} />
            <Route path="/trading-journal-template" element={<TradingJournalTemplatePage />} />
            <Route path="/trading-journal" element={<TradingJournalFeaturePage />} />
            <Route path="/forex-trading-journal" element={<ForexTradingJournalFeaturePage />} />
            <Route path="/ctrader-trading-journal" element={<CTraderJournalFeaturePage />} />
            <Route path="/mt5-trading-journal" element={<Mt5JournalFeaturePage />} />
            <Route path="/mt4-trading-journal" element={<Mt4JournalFeaturePage />} />
            <Route path="/free-trading-journal" element={<Navigate to="/trading-journal" replace />} />
            <Route path="/trading-diary" element={<TradingDiaryFeaturePage />} />
            <Route path="/trading-analytics" element={<Navigate to="/trading-journal-dashboard" replace />} />
            <Route path="/trading-journal-dashboard" element={<TradingJournalDashboardFeaturePage />} />
            <Route path="/ai-trading-chat" element={<AiTradingChatFeaturePage />} />
            <Route path="/ai-trading-report" element={<AiTradingReportFeaturePage />} />
            <Route path="/trading-tools" element={<TradingToolsHubPage />} />
            <Route path="/login" element={<LoginScreenSupabase onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/unsubscribed" element={<UnsubscribedPage />} />
            {APP_ONLY_PATHS.map((path) => (
              <Route key={path} path={path} element={<Navigate to="/login" replace />} />
            ))}
            <Route path="*" element={<PublicNotFoundPage />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  // (The onboarding-status wait now happens before the public/authenticated
  // split above, so a signed-in reload never mounts the landing page.)

  // The onboarding questionnaire used to sit here, six questions between signup
  // and the product. It was removed because it bought nothing: the answers were
  // saved, reloaded and passed to WelcomeTutorial, which declared `userProfile`
  // in its props and never read it. Six chances to close the tab, no
  // personalisation in return. If those answers are ever wanted again, ask after
  // the first import — once the product has given something back — and use them.

  // A recovery link signs the user in before a new password exists. Until one
  // is set, this session gets one page and nothing around it.
  //
  // Redirecting the routes was not enough, and the screenshot that proved it is
  // the point: the header, the file selector and the tours all render *outside*
  // `<Routes>`, so the app still looked open and its menu still worked while the
  // reset form sat in the middle. The block has to be here, above the shell —
  // not a guard inside it. `path="*"` rather than a redirect because there is
  // nowhere else to go: every URL is this page.
  if (resetPending) {
    return (
      <ThemeProvider>
        <ToastProvider>
          <ToastContainer />
          <Router>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="*" element={<ResetPasswordPage />} />
              </Routes>
            </Suspense>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    );
  }

  // User routes (authenticated users only)
  return (
    <ThemeProvider>
    <DialogProvider>
      <ToastProvider>
        <ToastContainer />
        <GlobalTradeProvider>
        <DataFilesProvider currentUser={currentUser}>
          <DiaryProvider>
            <Router>
              <UIStateManagerWrapper currentUser={currentUser} />
              <AuthenticatedRouteAnalyticsTracker currentUserId={currentUserId} />
              <AnalyticsConsentBanner />
              <AnalyticsRouteTracker />
              <div className="App">
                <Seo
                  title="TradingSpace App"
                  description="Private TradingSpace workspace."
                  path={window.location.pathname}
                  noIndex
                />
                <Navigation
                  onLogout={handleLogout}
                  currentUser={currentUser}
                />
                {/* ❌ REMOVED: PageTracker - only tracking logins/registrations now */}
                <Suspense fallback={<LoadingFallback />}>
                  <BroadcastNotificationBanner />
                </Suspense>

                {/* Demo mode banner — shown when user is exploring with demo data */}
                <Suspense fallback={null}>
                  <DemoBanner />
                </Suspense>

                {/* Shown when the trades could not be read. Sits above the demo
                    banner on purpose: "we could not reach the server" has to
                    win over anything that speaks about the account's contents. */}
                <Suspense fallback={null}>
                  <DataLoadErrorBanner />
                </Suspense>

                {/* The floating onboarding checklist was removed: it was the
                    fifth onboarding surface competing for the same screen, and
                    the only permanent one. */}

                {/* Anchored first-run tour. Runs once, before the per-page
                    intros, and opens on the import button because nothing else
                    in the product works until a file is in. */}
                <Suspense fallback={null}>
                  <FirstRunTourManager />
                </Suspense>

                {/* Connected accounts stay current while the app is open:
                    trades an EA sent appear without a reload, and cTrader
                    (which cannot push) is pulled at most every 10 minutes. */}
                <Suspense fallback={null}>
                  <PlatformAutoSync />
                </Suspense>

                {/* Two anchored steps the first time each page is opened, on
                    the page's own controls. Waits for the first-run tour, and
                    holds off on a page whose controls are not mounted yet
                    rather than marking it seen for a tour nobody saw. */}
                <Suspense fallback={null}>
                  <PageTourManager />
                </Suspense>

                {/* The beta offer. Mounted after the tours on purpose — it
                    waits for their portal to be gone before it opens, and
                    only speaks to free accounts, once. */}
                <Suspense fallback={null}>
                  <BetaOfferPopup />
                </Suspense>


                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/tajir" element={<TajirAI />} />
                    <Route path="/charts" element={<Navigate to="/tajir" replace />} />
                    <Route path="/data" element={<DataTable />} />
                    <Route path="/platforms" element={<PlatformsPage />} />
                    <Route path="/diary" element={<Diary />} />
                    <Route path="/report" element={<TradingReportPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/auth/ctrader/callback" element={<CTraderCallback />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                  </Routes>
                </Suspense>

                {/* The welcome tutorial used to sit here: five dark slides over
                    a light product, opening on a promise of "2 minutes" before
                    anything had been delivered. The anchored tour says the same
                    things pointing at the real controls, so this was the layer
                    with nothing left to do. */}
              </div>
            </Router>
          </DiaryProvider>
        </DataFilesProvider>
        </GlobalTradeProvider>
      </ToastProvider>
    </DialogProvider>
    </ThemeProvider>
  );

}

export default App;
